import { Injectable, NgZone } from "@angular/core";
import { AuthService } from "@auth0/auth0-angular";
import { firstValueFrom, Subscription } from "rxjs";
import { environment } from "../../environments/environment";
import { readSseStream } from "./sse-reader";

@Injectable({ providedIn: "root" })
export class AuthenticatedRealtimeService {
  constructor(private auth: AuthService, private zone: NgZone) {}

  private listeners = new Set<(event: { type: string; data: any }) => void>();
  private stopStream: (() => void) | null = null;

  // All screens share one authenticated connection for this browser application.
  subscribe(onEvent: (event: { type: string; data: any }) => void): () => void {
    this.listeners.add(onEvent);
    if (!this.stopStream) this.stopStream = this.openStream(event => {
      for (const listener of this.listeners) listener(event);
    });
    return () => {
      this.listeners.delete(onEvent);
      if (!this.listeners.size) {
        this.stopStream?.();
        this.stopStream = null;
      }
    };
  }

  // An authenticated stream, never a guest token or a token in a URL.
  private openStream(onEvent: (event: { type: string; data: any }) => void): () => void {
    let closed = false;
    let subject: string = null;
    let controller: AbortController = null;
    let retry: ReturnType<typeof setTimeout> = null;
    let rotation: ReturnType<typeof setTimeout> = null;
    const disconnect = () => {
      controller?.abort();
      controller = null;
      clearTimeout(retry);
      clearTimeout(rotation);
    };
    const connect = async () => {
      if (closed || !subject || controller || document.visibilityState === "hidden") return;
      const request = new AbortController();
      controller = request;
      try {
        const token = await firstValueFrom(this.auth.getAccessTokenSilently());
        if (closed || request.signal.aborted) return;
        const response = await fetch(`${environment.API_BASE_URL}/realtime/stream`, {
          headers: { Accept: "text/event-stream", Authorization: `Bearer ${token}` },
          signal: request.signal,
        });
        if (!response.ok || !response.body) throw new Error("Realtime unavailable");
        rotation = setTimeout(() => { disconnect(); connect(); }, 25 * 60 * 1000);
        await readSseStream(response.body, event => {
          if (!closed && !request.signal.aborted) this.zone.run(() => onEvent(event));
        }, () => closed || request.signal.aborted);
      } catch (_) {
        // The normal API request remains authoritative; reconnect without a login modal.
      } finally {
        if (controller === request) {
          controller = null;
          clearTimeout(rotation);
          if (!closed) retry = setTimeout(connect, 5000);
        }
      }
    };
    const visibility = () => {
      disconnect();
      connect();
    };
    document.addEventListener("visibilitychange", visibility);
    window.addEventListener("online", visibility);
    window.addEventListener("focus", visibility);
    const authSubscription: Subscription = this.auth.user$.subscribe(user => {
      const next = user?.sub || null;
      if (next !== subject) {
        disconnect();
        subject = next;
        connect();
      }
    });
    return () => {
      closed = true;
      disconnect();
      authSubscription.unsubscribe();
      document.removeEventListener("visibilitychange", visibility);
      window.removeEventListener("online", visibility);
      window.removeEventListener("focus", visibility);
    };
  }
}
